// import { appEventEmitter } from "../../../events/app-event-emitter";
import {
  INITIAL_MAP_FILTERS,
  INITIAL_GEOGRAPHICAL_FILTERS,
  INITIAL_FORM_FILTERS,
  FIND_LISTINGS_FORM_MODEL_DEFAULT_VALUES,
  PROPERTY_TYPE_ALL_LEASE_ID,
  PROPERTY_TYPE_ALL_SALE_ID,
} from "./helpers/property-search-filters-form-helpers";
import { FindListingsSchema } from "./helpers/property-search-filters-form-helpers";
import { stringToFloat } from "@cma/shared/helpers/form-converters";
import { getDateRangeDisplayLabel } from "./helpers/date-range-utils";
import { useGetPropertyTypes } from "@cma/shared/queries/property-search/useGetPropertyTypes";
import { useAuth } from "@shared/hooks/useAuth";
import { useCallback, useMemo, useState } from "react";
import { textFormatter } from "@shared/utils/text-formatter";
import { useGetPropertyStatuses } from "@cma/shared/queries/property-search/useGetPropertyStatuses";
import {
  normalizePropertyTypeIdsByMlsSource,
  isPropertyTypeDealType,
  getPropertyTypeDisplayName,
  isValidPropertyTypeId,
  mapCanonicalTypesToAliases,
} from "@cma/shared/helpers/dynamic-property-types-helpers";
import { TerritoryItem } from "@shared/apis/sidekick/generated";

interface Props {
  initialValues?: Partial<FindListingsSchema>;
  defaultCoords?: {
    lat: number;
    lng: number;
  };
}

export const useFindListingsSearchLogic = ({ initialValues }: Props) => {
  const [filtersState, setFiltersState] = useState<FindListingsSchema>({
    ...FIND_LISTINGS_FORM_MODEL_DEFAULT_VALUES,
    ...initialValues,
  });
  const { user } = useAuth();

  const { data: propertyTypes = [] } = useGetPropertyTypes({
    mlsSources: user.enabledMlsProviders ?? user.allowedMlsProviders,
  });

  const { data: propertyStatuses = [], isLoading: isLoadingPropertyTypes } =
    useGetPropertyStatuses();

  const resolvedDealType = useMemo((): "Lease" | "Sale" | null => {
    if (!filtersState?.propertyTypes?.length || propertyTypes.length === 0) {
      return null;
    }

    if (filtersState.propertyTypes.includes(PROPERTY_TYPE_ALL_LEASE_ID)) {
      return "Lease";
    }
    if (filtersState.propertyTypes.includes(PROPERTY_TYPE_ALL_SALE_ID)) {
      return "Sale";
    }

    const isLease = filtersState.propertyTypes.some((id) =>
      isPropertyTypeDealType(id, "Lease", propertyTypes),
    );

    return isLease ? "Lease" : "Sale";
  }, [filtersState, propertyTypes]);

  const filters = useMemo((): FindListingsSchema | null => {
    if (!propertyTypes.length) {
      return null;
    }

    const validPropertyTypes = filtersState.propertyTypes?.length
      ? filtersState.propertyTypes.filter((id) =>
          isValidPropertyTypeId(id, propertyTypes),
        )
      : [];

    const propertyTypesToUse =
      validPropertyTypes.length > 0
        ? validPropertyTypes
        : [PROPERTY_TYPE_ALL_SALE_ID];

    const aliasesPropertyTypes = mapCanonicalTypesToAliases(
      propertyTypesToUse,
      propertyTypes,
    );

    const normalizedPropertyTypes = normalizePropertyTypeIdsByMlsSource(
      aliasesPropertyTypes,
      propertyTypes,
      user.enabledMlsProviders,
    );

    return {
      ...FIND_LISTINGS_FORM_MODEL_DEFAULT_VALUES,
      ...filtersState,
      propertyTypes: normalizedPropertyTypes,
      dealType: resolvedDealType || undefined,
    };
  }, [filtersState, resolvedDealType, propertyTypes, user.enabledMlsProviders]);

  const resolvePropertyTypes = useCallback(
    (data: FindListingsSchema) => {
      const dealType = data.dealType as "Lease" | "Sale";
      const newPropertyTypes = data.propertyTypes?.filter((ptId) =>
        isPropertyTypeDealType(ptId, dealType, propertyTypes),
      );

      if (!newPropertyTypes || newPropertyTypes.length === 0) {
        if (data.dealType === "Lease") {
          return [PROPERTY_TYPE_ALL_LEASE_ID];
        } else if (data.dealType === "Sale") {
          return [PROPERTY_TYPE_ALL_SALE_ID];
        }
      }

      return newPropertyTypes;
    },
    [propertyTypes],
  );

  const handleApplyFilters = useCallback(
    async (input: FindListingsSchema) => {
      const data = { ...filtersState, ...input };

      // Map canonical property types to their first available alias
      if (data.propertyTypes?.length) {
        data.propertyTypes = mapCanonicalTypesToAliases(
          data.propertyTypes,
          propertyTypes,
        );
      }

      data.propertyTypes = resolvePropertyTypes(data);

      setFiltersState({
        ...filtersState,
        ...data,
        page: 1,
      });
    },
    [filtersState, resolvePropertyTypes, propertyTypes],
  );

  const handleClearFilters = (_presentationType: string) => {
    setFiltersState((old) => ({
      ...old,
      ...INITIAL_FORM_FILTERS,
      mapFilters: INITIAL_MAP_FILTERS,
    }));
    // appEventEmitter.emit({
    //   eventType: "cma-search-filters-cleared",
    //   presentationType,
    // });
  };

  /**
   * Map filter:
   *  Keep all existing filters except neighborhood and MLS area.
   *  Remove same building detection.
   **/
  const handleApplyMapFilters = (data: FindListingsSchema) => {
    const params: FindListingsSchema = {
      ...data,
      ...INITIAL_GEOGRAPHICAL_FILTERS,
      mapFilters: data.mapFilters,
      page: 1,
    };
    handleApplyFilters(params);
  };

  const handleApplyTerritoryFilters = (data: FindListingsSchema) => {
    const params: FindListingsSchema = {
      ...INITIAL_GEOGRAPHICAL_FILTERS,
      ...data,
      mapFilters: { ...INITIAL_MAP_FILTERS },
      page: 1,
    };
    handleApplyFilters(params);
  };

  const handleChangePage = useCallback(
    (input: Pick<FindListingsSchema, "page">) => {
      setFiltersState((oldState) => ({ ...oldState, ...input }));
    },
    [],
  );

  const readableAppliedFilters = useMemo((): Array<{
    id: string;
    label: string;
    removable?: boolean;
  }> => {
    const filters: Array<{ id: string; label: string; removable?: boolean }> =
      [];

    // Area/Location filters without polygon
    if (filtersState.street) {
      filters.push({
        id: "street",
        label: [filtersState.street, filtersState.city, filtersState.state]
          .filter(Boolean)
          .join(", "),
      });
    } else if (filtersState.streetAndNumber) {
      filters.push({
        id: "street-and-number",
        label: [
          filtersState.streetAndNumber,
          filtersState.city,
          filtersState.state,
        ]
          .filter(Boolean)
          .join(", "),
      });
    } else if (filtersState.zipCode) {
      filters.push({
        id: "zipCode",
        label: `ZIP: ${filtersState.zipCode}`,
      });
    }

    // Deal Type
    if (resolvedDealType) {
      filters.push({
        id: "dealType",
        label: resolvedDealType,
        removable: false,
      });
    }

    // Property status
    if (filtersState.propertyStatus?.length) {
      filtersState.propertyStatus.forEach((status) => {
        filters.push({
          id: `propertyStatus-${status}`,
          label:
            propertyStatuses.find((s) => status === s.value)?.label || status,
        });
      });
    }

    // Price range
    if (filtersState.priceMin) {
      const price = stringToFloat(filtersState.priceMin);
      filters.push({
        id: "priceMin",
        label: `Min: $${price.toLocaleString()}`,
      });
    }
    if (filtersState.priceMax) {
      const price = stringToFloat(filtersState.priceMax);
      filters.push({
        id: "priceMax",
        label: `Max: $${price.toLocaleString()}`,
      });
    }

    // Bedrooms
    if (filtersState.bedsMin) {
      const min = stringToFloat(filtersState.bedsMin);
      filters.push({
        id: "bedsMin",
        label: `Min: ${min} Beds`,
      });
    }
    if (filtersState.bedsMax) {
      const max = stringToFloat(filtersState.bedsMax);
      filters.push({
        id: "bedsMax",
        label: `Max: ${max} Beds`,
      });
    }

    // Bathrooms
    if (filtersState.bathsMin) {
      const min = stringToFloat(filtersState.bathsMin);
      filters.push({
        id: "bathsMin",
        label: `Min: ${min} Baths`,
      });
    }
    if (filtersState.bathsMax) {
      const max = stringToFloat(filtersState.bathsMax);
      filters.push({
        id: "bathsMax",
        label: `Max: ${max} Baths`,
      });
    }

    // Living Space
    if (filtersState.livingSpaceMin) {
      const min = stringToFloat(filtersState.livingSpaceMin);
      filters.push({
        id: "livingSpaceMin",
        label: `Min: ${textFormatter.area(min)}`,
      });
    }
    if (filtersState.livingSpaceMax) {
      const max = stringToFloat(filtersState.livingSpaceMax);
      filters.push({
        id: "livingSpaceMax",
        label: `Max: ${textFormatter.area(max)}`,
      });
    }

    // Lot Size
    if (filtersState.lotSizeMin) {
      const min = stringToFloat(filtersState.lotSizeMin);
      filters.push({
        id: "lotSizeMin",
        label: `Min lot: ${textFormatter.area(min)}`,
      });
    }
    if (filtersState.lotSizeMax) {
      const max = stringToFloat(filtersState.lotSizeMax);
      filters.push({
        id: "lotSizeMax",
        label: `Max lot: ${textFormatter.area(max)}`,
      });
    }

    // Garage Spaces
    if (filtersState.garageSpacesMin) {
      const min = stringToFloat(filtersState.garageSpacesMin);
      filters.push({
        id: "garageSpacesMin",
        label: `Min: ${min} Garage Spaces`,
      });
    }
    if (filtersState.garageSpacesMax) {
      const max = stringToFloat(filtersState.garageSpacesMax);
      filters.push({
        id: "garageSpacesMax",
        label: `Max: ${max} Garage Spaces`,
      });
    }

    // Year Built
    if (filtersState.yearBuiltMin) {
      const min = stringToFloat(filtersState.yearBuiltMin);
      filters.push({
        id: "yearBuiltMin",
        label: `Built: ${min} or later`,
      });
    }
    if (filtersState.yearBuiltMax) {
      const max = stringToFloat(filtersState.yearBuiltMax);
      filters.push({
        id: "yearBuiltMax",
        label: `Built: before ${max}`,
      });
    }

    // Property Types
    if (filtersState.propertyTypes?.length) {
      // Normalize property types to use MLS-specific mappings if available
      const normalizedTypes = normalizePropertyTypeIdsByMlsSource(
        filtersState.propertyTypes,
        propertyTypes,
        user.enabledMlsProviders,
      );
      const types = normalizedTypes.filter(Boolean);
      if (types.length > 0) {
        types.forEach((typeId) => {
          const displayName = getPropertyTypeDisplayName(typeId, propertyTypes);
          if (displayName) {
            filters.push({
              id: `propertyType-${typeId}`,
              label: displayName,
            });
          }
        });
      }
    }

    // List Date Range
    if (filtersState.listDateStart || filtersState.listDateEnd) {
      if (filtersState.listDateStart && filtersState.listDateEnd) {
        const label = getDateRangeDisplayLabel(
          filtersState.listDateStart,
          filtersState.listDateEnd,
        );
        filters.push({
          id: "listDateRange",
          label: `Listed ${label}`,
        });
      } else if (filtersState.listDateStart) {
        const startDate = filtersState.listDateStart.toLocaleDateString();
        filters.push({
          id: "listDateStart",
          label: `Listed from ${startDate}`,
        });
      } else if (filtersState.listDateEnd) {
        const endDate = filtersState.listDateEnd.toLocaleDateString();
        filters.push({
          id: "listDateEnd",
          label: `Listed until ${endDate}`,
        });
      }
    }

    // Sold Date Range
    if (filtersState.soldDateStart || filtersState.soldDateEnd) {
      if (filtersState.soldDateStart && filtersState.soldDateEnd) {
        const label = getDateRangeDisplayLabel(
          filtersState.soldDateStart,
          filtersState.soldDateEnd,
        );
        filters.push({
          id: "soldDateRange",
          label: `Sold ${label}`,
        });
      } else if (filtersState.soldDateStart) {
        const startDate = filtersState.soldDateStart.toLocaleDateString();
        filters.push({
          id: "soldDateStart",
          label: `Sold from ${startDate}`,
        });
      } else if (filtersState.soldDateEnd) {
        const endDate = filtersState.soldDateEnd.toLocaleDateString();
        filters.push({
          id: "soldDateEnd",
          label: `Sold until ${endDate}`,
        });
      }
    }

    // Doorman
    if (filtersState.doorman === "yes") {
      filters.push({
        id: "doorman",
        label: "Doorman",
      });
    } else if (filtersState.doorman === "no") {
      filters.push({
        id: "doorman",
        label: "No Doorman",
      });
    }

    // Waterfront
    if (filtersState.waterfrontClassification === "waterfront") {
      filters.push({
        id: "waterfrontClassification",
        label: "Waterfront",
      });
    }

    return filters;
  }, [
    filtersState,
    propertyTypes,
    resolvedDealType,
    user.enabledMlsProviders,
    propertyStatuses,
  ]);

  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      const updatedFilters = { ...filtersState };
      switch (filterId) {
        case "neighborhood":
          updatedFilters.neighborhood = "";
          break;
        case "county":
          updatedFilters.county = "";
          break;
        case "district":
          updatedFilters.district = "";
          break;
        case "subDistrict":
          updatedFilters.subDistrict = "";
          break;
        case "schoolDistrict":
          updatedFilters.schoolDistrict = "";
          break;
        case "city":
          updatedFilters.city = "";
          break;
        case "city-state":
          updatedFilters.city = "";
          updatedFilters.state = "";
          break;
        case "dealType":
          updatedFilters.dealType = undefined;
          break;
        case "priceMin":
          updatedFilters.priceMin = "";
          break;
        case "priceMax":
          updatedFilters.priceMax = "";
          break;
        case "bedsMin":
          updatedFilters.bedsMin = "";
          break;
        case "bedsMax":
          updatedFilters.bedsMax = "";
          break;
        case "bathsMin":
          updatedFilters.bathsMin = "";
          break;
        case "bathsMax":
          updatedFilters.bathsMax = "";
          break;
        case "livingSpaceMin":
          updatedFilters.livingSpaceMin = "";
          break;
        case "livingSpaceMax":
          updatedFilters.livingSpaceMax = "";
          break;
        case "lotSizeMin":
          updatedFilters.lotSizeMin = "";
          break;
        case "lotSizeMax":
          updatedFilters.lotSizeMax = "";
          break;
        case "garageSpacesMin":
          updatedFilters.garageSpacesMin = "";
          break;
        case "garageSpacesMax":
          updatedFilters.garageSpacesMax = "";
          break;
        case "yearBuiltMin":
          updatedFilters.yearBuiltMin = "";
          break;
        case "yearBuiltMax":
          updatedFilters.yearBuiltMax = "";
          break;
        case "listDateStart":
          updatedFilters.listDateStart = null;
          break;
        case "listDateEnd":
          updatedFilters.listDateEnd = null;
          break;
        case "listDateRange":
          updatedFilters.listDateStart = null;
          updatedFilters.listDateEnd = null;
          break;
        case "soldDateStart":
          updatedFilters.soldDateStart = null;
          break;
        case "soldDateEnd":
          updatedFilters.soldDateEnd = null;
          break;
        case "soldDateRange":
          updatedFilters.soldDateStart = null;
          updatedFilters.soldDateEnd = null;
          break;
        case "doorman":
          updatedFilters.doorman = "";
          break;
        case "waterfrontClassification":
          updatedFilters.waterfrontClassification = null;
          break;
        default:
          // Handle property status filters (e.g., "propertyStatus-for-sale")
          if (filterId.startsWith("propertyStatus-")) {
            const statusValue = filterId.replace("propertyStatus-", "");
            updatedFilters.propertyStatus = (
              updatedFilters.propertyStatus || []
            ).filter((status) => status !== statusValue);
          }
          // Handle property type filters (e.g., "propertyType-123")
          else if (filterId.startsWith("propertyType-")) {
            const typeId = filterId.replace("propertyType-", "");
            updatedFilters.propertyTypes = (
              updatedFilters.propertyTypes || []
            ).filter((id) => id !== typeId);
          }
          break;
      }

      handleApplyFilters(updatedFilters);
    },
    [filtersState, handleApplyFilters],
  );

  const territorySearchValue = useMemo((): TerritoryItem | null => {
    if (!filters) return null;
    const state = filters.state || "";
    if (filters.neighborhood) {
      return {
        type: "neighborhood",
        name: filters.neighborhood,
        city: filters.city,
        state,
      };
    }
    if (filters.county) {
      return {
        type: "county",
        name: filters.county,
        state,
      };
    }
    if (filters.district) {
      return {
        type: "district",
        name: filters.district,
        city: filters.city,
        state,
      };
    }
    if (filters.subDistrict) {
      return {
        type: "sub-district",
        name: filters.subDistrict,
        city: filters.city,
        state,
      };
    }
    if (filters.schoolDistrict) {
      return {
        type: "school-district",
        name: filters.schoolDistrict,
        state,
      };
    }
    if (filters.city) {
      return {
        type: "city",
        name: filters.city,
        state,
      };
    }
    return null;
  }, [filters]);

  return {
    filters,
    territorySearchValue,
    readableAppliedFilters,
    propertyTypes,
    isLoadingPropertyTypes,
    propertyStatuses,
    handleApplyFilters,
    handleClearFilters,
    handleApplyMapFilters,
    handleApplyTerritoryFilters,
    handleChangePage,
    handleRemoveFilter,
  };
};
